import { Component, OnDestroy, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { combineLatest, of, Subscription, race, timer } from 'rxjs'
import { finalize, switchMap, take, mapTo } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { ApplicationDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { AccessLevelService, AccountPermission } from 'src/app/services/access-level.service'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from '../../services/account.service'
import { ApplicationProviderService } from '../../services/application.service'

@Component({
  selector: 'app-account-root',
  templateUrl: './account-root.component.html',
  styleUrls: ['./account-root.component.scss'],
  standalone: false,
})
export class AccountRootComponent implements OnInit, OnDestroy {
  applications: Array<ApplicationDto> = null
  private applicationSubscription: Subscription = null
  private permissionSubscription: Subscription = null
  public isAccountAdmin = false
  // Track whether we've completed the initial application check
  public initialCheckComplete = false

  constructor(
    private accountService: AccountService,
    public apService: ApplicationProviderService,
    private apiService: ApiService,
    private uiService: UiService,
    private accessLevelService: AccessLevelService,
    private router: Router
  ) {}

  ngOnInit() {
    // Check if user is account admin
    this.permissionSubscription = this.accessLevelService
      .hasAccountPermission(AccountPermission.MANAGE_RBAC)
      .subscribe((hasManageRbac) => {
        this.isAccountAdmin = hasManageRbac
      })

    // Subscribe to the stream of application update events
    this.applicationSubscription = this.accountService.accountIdStream
      .pipe(
        switchMap((accountId) => {
          // Race between the first application event and a 1500ms timeout.
          // New accounts with zero applications will never emit an application event,
          // so the timer acts as a fallback to route to the welcome page.
          return race(
            combineLatest([of(accountId), this.apService.subscribeToApplications$(accountId)]).pipe(take(1)),
            timer(1200).pipe(mapTo([accountId, null]))
          )
        }),
        take(1),
        finalize(() => {
          this.applicationSubscription = null
          // Mark the initial check as complete
          this.initialCheckComplete = true
        })
      )
      .subscribe(([accountId, firstApplicationCrudEvent]: [string, any]) => {
        if (firstApplicationCrudEvent && firstApplicationCrudEvent.entityId) {
          // Navigate to the first application
          this.router.navigateByUrl(this.uiService.buildApplicationUrl(accountId, firstApplicationCrudEvent.entityId))
        } else {
          // No applications exist, navigate to welcome/landing page
          this.router.navigateByUrl(this.uiService.buildAccountWelcomeUrl(accountId))
        }
      })
  }

  ngOnDestroy() {
    if (subscriptionActive(this.applicationSubscription)) {
      this.applicationSubscription.unsubscribe()
      this.applicationSubscription = null
    }
    if (subscriptionActive(this.permissionSubscription)) {
      this.permissionSubscription.unsubscribe()
      this.permissionSubscription = null
    }
  }
}
